// File-based backup & transfer - no cloud, no account, no store. Everything is a
// local file the user downloads/opens: a SAVEFILE (all Re:Vault data) and LOOK
// files (a single shareable look, handled in bundles.js). Plus a complete wipe.

import { WALLPAPER_KEY, SETTINGS_KEY } from "./settings-schema.js";
import { setWallpaperImageData } from "./wallpaper-image.js";
import { WARNINGS, leaveOff, setConsent } from "./consent.js";
import { SECRET, NO_SYNC, DEVICE, FIELD_FILTER, keepFor, filterFields, mergeFields } from "./save-keys.js";

const PREFIX = "revault:";

// The key lists live in save-keys.js; these names stay for older imports.
export const SECRET_KEYS = SECRET;
export const NO_SYNC_KEYS = NO_SYNC;
// Device-only keys never go in a cloud backup; a downloaded save file keeps only the saved tabs.
export const DEVICE_ONLY_KEYS = DEVICE;
const skipKey = (k, cloud) => !keepFor(k, cloud ? "cloud" : "file");

// Download an object as a .json file.
export function downloadJSON(filename, obj) {
	const blob = new Blob([JSON.stringify(obj)], { type: "application/json" });
	const url = URL.createObjectURL(blob);
	const a = document.createElement("a");
	a.href = url;
	a.download = filename;
	document.body.appendChild(a);
	a.click();
	a.remove();
	setTimeout(() => URL.revokeObjectURL(url), 2000);
}

// Open a file picker and parse the chosen JSON. Calls onObj(obj, error).
export function pickJSONFile(onObj) {
	const inp = document.createElement("input");
	inp.type = "file";
	inp.accept = "application/json,.json";
	inp.style.display = "none";
	inp.addEventListener("change", () => {
		const f = inp.files && inp.files[0];
		inp.remove();
		if (!f) return;
		const r = new FileReader();
		r.onload = () => { try { onObj(JSON.parse(String(r.result)), null); } catch (e) { onObj(null, e); } };
		r.onerror = () => onObj(null, new Error("read"));
		r.readAsText(f);
	});
	document.body.appendChild(inp);
	inp.click();
}

// SAVEFILE = every revault:* localStorage entry (settings, favorites, history,
// session, saved looks, AND the uploaded wallpaper image). Captures everything
// except the proxied-site caches (those are disposable and rebuilt on use).
// { cloud: true } leaves out the device-only keys and the device-only fields (save-keys.js).
export function collectSave({ cloud = false } = {}) {
	const data = {};
	try {
		for (let i = 0; i < localStorage.length; i++) {
			const k = localStorage.key(i);
			if (!k || !k.startsWith(PREFIX) || skipKey(k, cloud)) continue;
			const v = localStorage.getItem(k);
			data[k] = cloud ? filterFields(k, v) : v;
		}
	} catch (e) {}
	return { _rv: "save", v: 1, data };
}

// Returns false for a file that isn't a backup, otherwise { leftOff }: the names of settings that
// were on in the backup but need a yes on this browser first, so they stay off (consent.js).
export function applySave(obj, { cloud = false } = {}) {
	if (!obj || obj._rv !== "save" || !obj.data || typeof obj.data !== "object") return false;
	let leftOff = [];
	try {
		for (const k in obj.data) {
			if (!k.startsWith(PREFIX) || typeof obj.data[k] !== "string" || skipKey(k, cloud)) continue;
			if (k === WALLPAPER_KEY) {
				// The wallpaper image data URL is later interpolated raw into a CSS url(...)
				// in the Appearance gallery, so an imported savefile must clear the same
				// strict base64 gate the look-file path uses - otherwise a crafted "backup"
				// could smuggle a CSS value-injection / external-fetch payload. The stored
				// value is JSON.stringify'd; parse to the bare data URL, then re-validate.
				let img;
				try { img = JSON.parse(obj.data[k]); } catch (e) { img = null; }
				if (typeof img === "string") setWallpaperImageData(img); // re-stores only if it validates
				continue;
			}
			// Another device's Study Helper pass, models and speed never land here.
			if (FIELD_FILTER[k]) { localStorage.setItem(k, mergeFields(k, localStorage.getItem(k), obj.data[k])); continue; }
			if (k === SETTINGS_KEY) {
				// The restored settings replace these, so an earlier yes no longer counts: turning
				// one on again asks again.
				for (const w of Object.keys(WARNINGS)) setConsent(w, false);
				let s;
				try { s = JSON.parse(obj.data[k]); } catch (e) { s = null; }
				if (s && typeof s === "object" && !Array.isArray(s)) {
					leftOff = leaveOff(s);
					if (leftOff.length) { localStorage.setItem(k, JSON.stringify(s)); continue; }
				}
			}
			localStorage.setItem(k, obj.data[k]);
		}
	} catch (e) { return false; }
	return { leftOff };
}

// Complete destruction: wipe ALL local data - localStorage/sessionStorage, the
// proxied-content caches, IndexedDB, cookies, and the service worker.
export async function wipeEverything() {
	try { localStorage.clear(); } catch (e) {}
	try { sessionStorage.clear(); } catch (e) {}
	try { if (window.caches) { const ks = await caches.keys(); await Promise.all(ks.map((k) => caches.delete(k))); } } catch (e) {}
	try { if (window.indexedDB && indexedDB.databases) { const dbs = await indexedDB.databases(); for (const d of dbs) if (d.name) indexedDB.deleteDatabase(d.name); } } catch (e) {}
	try { document.cookie.split(";").forEach((c) => { const n = c.split("=")[0].trim(); if (n) document.cookie = n + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/"; }); } catch (e) {}
	try { if (navigator.serviceWorker) { const rs = await navigator.serviceWorker.getRegistrations(); await Promise.all(rs.map((r) => r.unregister())); } } catch (e) {}
}

// Reload the whole app onto a clean slate. The settings page runs inside an
// iframe, so target the top window and skip the close-confirm.
export function reloadApp() {
	let w = window;
	try { w = window.top || window; } catch (e) { w = window; }
	try { w.__rvUpdating = true; } catch (e) {}
	try { w.location.replace("/"); } catch (e) { location.replace("/"); }
}
